import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatNumber } from '../ui/format';
import { Badge, EmptyState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { useFeature } from '../app/entitlements';
import {
  activateAutomation,
  createAutomation,
  listAutomations,
  listRuns,
  listTriggers,
  pauseAutomation,
  type AutomationView,
  type RunView,
} from './api';

/**
 * Automations and what they have actually done.
 *
 * The run log is the point. An automation that "is active" tells you nothing; a list of runs with
 * their outcomes tells you whether it works, and a failure count beside the name is what makes a
 * broken one visible before a customer finds it (context.md sections 84-90).
 *
 * Pausing is on the row rather than only inside the builder, because it is the action somebody
 * reaches for at 2am; everything else about an automation is a click away on its own screen.
 */

const TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  PAUSED: 'warning',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

const RUN_TONE: Record<string, BadgeTone> = {
  COMPLETED: 'positive',
  RUNNING: 'info',
  PENDING: 'neutral',
  WAITING: 'warning',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  STOPPED: 'neutral',
};

export function AutomationsPage(): React.JSX.Element {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const automations = useQuery({ queryKey: ['automations'], queryFn: () => listAutomations() });

  /*
   * The plan decides whether this tenant may build automations at all.
   *
   * Asked here so the answer arrives before somebody fills in a name, rather than as a 402 after
   * pressing create. The server refuses either way; this is only about not wasting their time.
   */
  const { allowed, planName } = useFeature('AUTOMATIONS');

  const toggle = useMutation({
    mutationFn: (automation: AutomationView) =>
      automation.status === 'ACTIVE'
        ? pauseAutomation(automation.id)
        : activateAutomation(automation.id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['automations'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<AutomationView>[] = [
    {
      key: 'name',
      header: t('automations.name'),
      /*
       * The trigger under the name, in words.
       *
       * It used to show the event key — `cs.account.renewal_due` — which is the bus's name for the
       * thing, not the product's, and is exactly the kind of string the dictionary exists to keep
       * off a screen (context.md §130). The key stays as the title, for whoever is debugging.
       */
      text: (automation) => triggerLabel(automation.triggerType),
      render: (automation) => (
        <span className="cell-stack">
          <span className="strong">{automation.name}</span>
          <span className="muted small" title={automation.triggerType}>
            {triggerLabel(automation.triggerType)}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (automation) => (
        <Badge tone={TONE[automation.status] ?? 'neutral'}>
          {t(`automations.state.${automation.status}`)}
        </Badge>
      ),
    },
    {
      key: 'runs',
      header: t('automations.runs'),
      align: 'end',
      render: (automation) => (
        <span className="cell-stack cell-stack--end">
          <span>{formatNumber(automation.runCount)}</span>
          {automation.failureCount > 0 && (
            <span className="muted small text-down">
              {t('automations.failures').replace('{n}', String(automation.failureCount))}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'last',
      header: t('automations.lastRun'),
      secondary: true,
      align: 'end',
      render: (automation) => formatDateTime(automation.lastRunAt),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (automation) => (
        <span className="cell-inline">
          <Link
            className="button button--quiet button--compact"
            to={`/automacoes/${automation.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {t('automations.open')}
          </Link>

          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={
              toggle.isPending || (!automation.publishedVersionId && automation.status !== 'ACTIVE')
            }
            onClick={(event) => {
              event.stopPropagation();
              toggle.mutate(automation);
            }}
          >
            {automation.status === 'ACTIVE' ? t('automations.pause') : t('automations.activate')}
          </button>
        </span>
      ),
    },
  ];

  return (
    <div className="view">
      <PageHeader
        title={t('nav.automations')}
        description={t('automations.description')}
        actions={
          allowed === false ? (
            <Badge tone="warning">
              {t('automations.notInPlan').replace('{plan}', planName ?? '')}
            </Badge>
          ) : (
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={allowed === undefined}
              onClick={() => setCreating(true)}
            >
              {t('automations.new')}
            </button>
          )
        }
      />

      <NewAutomation
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => navigate(`/automacoes/${id}`)}
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <Panel>
        <DataTable
          tableKey="automations"
          rows={automations.data?.automations}
          columns={columns}
          rowKey={(automation) => automation.id}
          isLoading={automations.isLoading}
          error={automations.error}
          onRetry={() => void automations.refetch()}
          onRowClick={(automation) =>
            setSelected((current) => (current === automation.id ? null : automation.id))
          }
          empty={{ title: t('automations.empty'), description: t('automations.emptyBody') }}
        />
      </Panel>

      {selected && <RunLog automationId={selected} />}
    </div>
  );
}

/**
 * Starting one.
 *
 * A name and a trigger, and nothing else: the steps are the work and they belong on the builder,
 * where there is room. Asking for them in a dialog would produce a dialog people abandon.
 *
 * It opens as a draft. Nothing runs until somebody activates it on the next screen, which is why
 * creating one is safe enough to be one click.
 */
function NewAutomation({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}): React.JSX.Element | null {
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState('crm.lead.created');
  const [error, setError] = useState<string | null>(null);

  const triggers = useQuery({
    queryKey: ['automation-triggers'],
    queryFn: listTriggers,
    enabled: open,
    staleTime: 60 * 60 * 1000,
  });

  const create = useMutation({
    mutationFn: () => createAutomation({ name: name.trim(), triggerType }),
    onSuccess: (automation) => {
      setName('');
      setError(null);
      onClose();
      onCreated(automation.id);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (!open) return null;

  return (
    <Panel title={t('automations.new')}>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <label className="field-search">
          <input
            value={name}
            autoFocus
            maxLength={200}
            placeholder={t('automations.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <select
          className="select"
          value={triggerType}
          aria-label={t('automations.trigger')}
          onChange={(event) => setTriggerType(event.target.value)}
        >
          {(triggers.data?.triggers ?? []).map((spec) => (
            <option key={spec.type} value={spec.type}>
              {`${t(`automations.area.${spec.area}`)} · ${t(`automations.trigger.${spec.type}`)}`}
            </option>
          ))}
        </select>

        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={name.trim() === '' || create.isPending}
        >
          {create.isPending ? t('work.creating') : t('automations.create')}
        </button>

        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('confirm.cancel')}
        </button>
      </form>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </Panel>
  );
}

function RunLog({ automationId }: { automationId: string }): React.JSX.Element {
  const runs = useQuery({
    queryKey: ['runs', automationId],
    queryFn: () => listRuns(automationId),
  });

  const columns: Column<RunView>[] = [
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (run) => (
        <span className="cell-stack">
          <Badge tone={RUN_TONE[run.status] ?? 'neutral'}>
            {t(`automations.run.${run.status}`)}
          </Badge>
          {run.error && <span className="muted small text-down">{run.error}</span>}
        </span>
      ),
    },
    {
      key: 'started',
      header: t('automations.startedAt'),
      render: (run) => formatDateTime(run.startedAt),
    },
    {
      key: 'finished',
      header: t('automations.finishedAt'),
      secondary: true,
      render: (run) =>
        run.finishedAt
          ? formatDateTime(run.finishedAt)
          : run.resumeAt
            ? t('automations.resumesAt').replace('{when}', formatDateTime(run.resumeAt))
            : '—',
    },
    {
      key: 'depth',
      header: t('automations.depth'),
      secondary: true,
      align: 'end',
      render: (run) => String(run.depth),
    },
  ];

  return (
    <Panel title={t('automations.runLog')}>
      {runs.isLoading ? (
        <Loading />
      ) : (runs.data?.runs.length ?? 0) === 0 ? (
        <EmptyState title={t('automations.noRuns')} description={t('automations.noRunsBody')} />
      ) : (
        <DataTable
          tableKey="automations-2"
          rows={runs.data?.runs}
          columns={columns}
          rowKey={(run) => run.id}
          error={runs.error}
          onRetry={() => void runs.refetch()}
          empty={{ title: t('automations.noRuns') }}
        />
      )}
    </Panel>
  );
}

/**
 * A trigger's name, or the event key when this build has no word for it.
 *
 * The fallback matters: the catalogue is published by the server, so a release that adds a trigger
 * before the dictionary catches up would otherwise render an empty cell rather than something
 * somebody can at least search for. `i18n.keys.test.ts` is what stops that from lasting.
 */
function triggerLabel(type: string): string {
  const key = `automations.trigger.${type}`;
  const label = t(key);

  return label === key ? type : label;
}
